import { useCallback, useEffect, useState } from "react";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import { Flex, Icon, Icons, Spin, Switch, useToast } from "@ir/ui";
import { Permissions } from "@src/domain/permissions/Permissions";
import { GraphQlAdapter } from "@src/infrastructure/adapters/graphQl/GraphQl.adapter";
import { BrandRepository } from "@src/infrastructure/repositories/brand";
import { UpdateBrandFeatureStatusDTO } from "@src/domain/dto/brands";
import { UpdateBrandFeatureStatusUseCase } from "@src/application/useCases/brand";
import type { BrandFeature } from "@src/domain/entities/brand";

export interface BrandFeatureStatusUpdateProps {
  brandId: number;
  featureId: number;
  isActive: boolean;
  onUpdateSuccess?: (feature: BrandFeature) => void;
}

export const BrandFeatureStatusUpdate = ({
  brandId,
  featureId,
  isActive,
  onUpdateSuccess,
}: BrandFeatureStatusUpdateProps) => {
  const [pendingActive, setPendingActive] = useState(isActive);
  const [isUpdating, setIsUpdating] = useState(false);
  const { error: errorToast, success: successToast } = useToast();

  useEffect(() => {
    setPendingActive(isActive);
  }, [isActive]);

  const handleSwitchChange = useCallback(
    async (mode: boolean) => {
      setPendingActive(mode);
      setIsUpdating(true);
      try {
        const payload = { brandId, featureId, isActive: mode };
        const validated = UpdateBrandFeatureStatusDTO.validate(payload);
        if (!validated.success) {
          console.error(
            "UpdateBrandFeatureStatus validation failed",
            validated.error,
          );
          throw new Error("UpdateBrandFeatureStatus validation failed");
        }

        const dto = new UpdateBrandFeatureStatusDTO(validated.data);
        const updatedFeature = await new UpdateBrandFeatureStatusUseCase(
          new BrandRepository(GraphQlAdapter.getInstance().getClient()),
        ).execute(dto);

        onUpdateSuccess?.(updatedFeature);
        successToast({
          content: "Feature status updated",
          icon: <Icon name={Icons.CheckCircle} />,
        });
      } catch (error) {
        console.error("Failed to update brand feature status", error);
        setPendingActive(isActive);
        errorToast({
          content: "Failed to update feature status",
          icon: <Icon name={Icons.AlertCircle} />,
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [brandId, featureId, isActive, onUpdateSuccess, successToast, errorToast],
  );

  return (
    <HasAnyPermission requiredPermissions={[Permissions.BRANDS_EDIT]}>
      <Flex align="center" gap={0.5}>
        {isUpdating && <Spin size="small" spinning />}
        <Switch
          size="small"
          isSelected={pendingActive}
          onChange={handleSwitchChange}
          isDisabled={isUpdating}
          aria-label="Brand feature status update"
          role="switch"
          htmlTitle="Update feature status"
        />
      </Flex>
    </HasAnyPermission>
  );
};
